import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { MAIN_SITE_URL, categories } from '@/mocks/categories';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'pl', label: 'Polski' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
  };

  const scrollLinks = [
    { key: 'spreadsheet', id: 'spreadsheet' },
    { key: 'categories', id: 'browse' },
    { key: 'guide', id: 'guides' },
    { key: 'safety', id: 'safety' },
    { key: 'shopGuide', id: 'browse' },
  ];

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-stone-200 bg-white">
      <div className="px-4 md:px-6">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap font-display text-lg font-bold text-stone-900">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-white">
              <i className="ri-links-line text-lg"></i>
            </span>
            <span>{t('brand')}</span>
          </Link>

          <nav className="hidden items-center gap-0.5 lg:flex">
            <Link
              to="/"
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                isHome ? 'text-emerald-700' : 'text-stone-700 hover:bg-stone-100 hover:text-stone-900'
              }`}
            >
              {t('nav.home')}
            </Link>
            {scrollLinks.map((link) => (
              <button
                key={link.key}
                type="button"
                onClick={() => scrollTo(link.id)}
                className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-100 hover:text-stone-900"
              >
                {t(`nav.${link.key}`)}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                type="button"
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-global-line text-base"></i>
                </span>
                <span className="uppercase">{i18n.language}</span>
                <i className="ri-arrow-down-s-line text-base"></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 w-40 overflow-hidden rounded-lg border border-stone-200 bg-white shadow-sm">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => changeLanguage(lang.code)}
                      className={`flex w-full items-center justify-between whitespace-nowrap px-4 py-2.5 text-left text-sm transition-colors hover:bg-stone-50 ${
                        i18n.language === lang.code ? 'font-semibold text-emerald-700' : 'text-stone-700'
                      }`}
                    >
                      {lang.label}
                      {i18n.language === lang.code && <i className="ri-check-line"></i>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="hidden whitespace-nowrap rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 sm:inline-flex"
            >
              {t('hero.ctaSecondary')}
            </a>

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-stone-200 bg-white text-stone-700 transition-colors lg:hidden"
              aria-label="Menu"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-stone-200 bg-white px-4 py-3 lg:hidden">
          <nav className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-2.5 text-left text-sm font-semibold text-emerald-700 hover:bg-stone-50"
            >
              <i className="ri-home-4-line mr-2"></i>
              {t('nav.home')}
            </Link>

            {scrollLinks.map((link) => (
              <button
                key={link.key}
                type="button"
                onClick={() => scrollTo(link.id)}
                className="rounded-md px-3 py-2.5 text-left text-sm font-medium text-stone-700 hover:bg-stone-50"
              >
                {t(`nav.${link.key}`)}
              </button>
            ))}

            {/* Category accordion */}
            <div className="mt-1 overflow-hidden rounded-md border border-stone-100">
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm font-semibold text-stone-800 hover:bg-stone-50"
              >
                <span>
                  <i className="ri-grid-line mr-2 text-emerald-700"></i>
                  {t('nav.categories')}
                </span>
                <span className="flex h-5 w-5 items-center justify-center text-stone-500">
                  <i className={`${catOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`}></i>
                </span>
              </button>
              {catOpen && (
                <div className="grid grid-cols-2 gap-1 border-t border-stone-100 bg-stone-50/50 p-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-stone-700 transition-colors hover:bg-white hover:text-emerald-700"
                    >
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500"></span>
                      <span className="truncate">{t(`categories.${cat.key}`)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/usfans-spreadsheet-guide"
              onClick={() => setMobileOpen(false)}
              className="mt-1 rounded-md px-3 py-2.5 text-left text-sm font-medium text-emerald-700 hover:bg-stone-50"
            >
              <i className="ri-book-open-line mr-2"></i>
              {t('featuredGuide.readMore')}
            </Link>

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 rounded-md bg-emerald-600 px-3 py-2.5 text-center text-sm font-semibold text-white"
            >
              {t('hero.ctaSecondary')}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}